<template>
  <dashboard-layout title="Client Log" back-to="/client-logs" back-label="Client Logs">
    <loading-state v-if="loading" label="Loading log details…" />

    <template v-else-if="log">
      <data-panel title="Basic Information">
        <div class="detail-grid">
          <div class="detail-item">
            <label>Service</label>
            <div class="value service">{{ log.service || '-' }}</div>
          </div>
          <div class="detail-item">
            <label>Status</label>
            <div class="value">
              <status-badge :variant="statusVariant(log.status)" :label="log.status || '-'" />
            </div>
          </div>
          <div class="detail-item">
            <label>Created At</label>
            <div class="value">{{ formatTimestamp(log.created_at) }}</div>
          </div>
          <div class="detail-item">
            <label>User ID</label>
            <router-link :to="`/user/${log.user_id}`" class="value user-link">
              {{ log.user_id || '-' }}
            </router-link>
          </div>
        </div>
      </data-panel>

      <data-panel v-if="parsedError && parsedError.error" title="Error Details">
        <div class="detail-grid">
          <div class="detail-item full-width">
            <label>Error Description</label>
            <div class="value error-desc">{{ parsedError.error.description || '-' }}</div>
          </div>
          <div class="detail-item">
            <label>Error Code</label>
            <div class="value">{{ parsedError.error.code || '-' }}</div>
          </div>
          <div class="detail-item">
            <label>Error Domain</label>
            <div class="value">{{ parsedError.error.domain || '-' }}</div>
          </div>
          <div class="detail-item">
            <label>Error Type</label>
            <div class="value">{{ parsedError.error.type || '-' }}</div>
          </div>
        </div>
      </data-panel>

      <data-panel v-if="parsedError && parsedError.additionalInfo" title="Additional Information">
        <div class="json-display">
          <pre>{{ JSON.stringify(parsedError.additionalInfo, null, 2) }}</pre>
        </div>
      </data-panel>

      <data-panel v-if="parsedError && parsedError.app" title="App Information">
        <div class="detail-grid">
          <div class="detail-item">
            <label>Version</label>
            <div class="value">{{ parsedError.app.version || '-' }}</div>
          </div>
          <div class="detail-item">
            <label>Build Number</label>
            <div class="value">{{ parsedError.app.buildNumber || '-' }}</div>
          </div>
          <div class="detail-item full-width">
            <label>Bundle ID</label>
            <div class="value">{{ parsedError.app.bundleId || '-' }}</div>
          </div>
        </div>
      </data-panel>

      <data-panel v-if="parsedError && parsedError.device" title="Device Information">
        <div class="detail-grid">
          <div class="detail-item">
            <label>Device Model</label>
            <div class="value">{{ parsedError.device.model || '-' }}</div>
          </div>
          <div class="detail-item">
            <label>Device Name</label>
            <div class="value">{{ parsedError.device.name || '-' }}</div>
          </div>
          <div class="detail-item">
            <label>System Name</label>
            <div class="value">{{ parsedError.device.systemName || '-' }}</div>
          </div>
          <div class="detail-item">
            <label>System Version</label>
            <div class="value">{{ parsedError.device.systemVersion || '-' }}</div>
          </div>
          <div class="detail-item full-width">
            <label>Identifier For Vendor</label>
            <div class="value">{{ parsedError.device.identifierForVendor || '-' }}</div>
          </div>
        </div>
      </data-panel>

      <data-panel title="Raw Error Message">
        <div class="json-display">
          <pre>{{ log.error_message }}</pre>
        </div>
      </data-panel>
    </template>

    <empty-state v-else title="Log not found">
      <router-link to="/client-logs" class="user-link">Return to Client Logs</router-link>
    </empty-state>
  </dashboard-layout>
</template>

<script>
import { ref, onMounted, computed } from 'vue'
import { useRoute } from 'vue-router'
import { FirebaseLogService } from '../services/firebaseLogService'
import DashboardLayout from './ui/DashboardLayout.vue'
import DataPanel from './ui/DataPanel.vue'
import StatusBadge from './ui/StatusBadge.vue'
import LoadingState from './ui/LoadingState.vue'
import EmptyState from './ui/EmptyState.vue'

export default {
  name: 'ClientLogDetailView',
  components: {
    DashboardLayout, DataPanel, StatusBadge, LoadingState, EmptyState
  },
  setup() {
    const route = useRoute()
    const service = new FirebaseLogService()
    const log = ref(null)
    const loading = ref(true)

    const parsedError = computed(() => {
      if (!log.value || !log.value.error_message) return null
      try {
        return JSON.parse(log.value.error_message)
      } catch {
        return null
      }
    })

    const loadLog = async () => {
      loading.value = true
      try {
        const logData = await service.getLogById(route.params.id)
        log.value = logData
      } catch (e) {
        console.error('Error loading log:', e)
      } finally {
        loading.value = false
      }
    }

    const formatTimestamp = ts => {
      if (!ts) return '-'
      if (ts.seconds) {
        const d = new Date(ts.seconds * 1000)
        return d.toLocaleString('tr-TR', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit'
        })
      }
      const d = new Date(ts)
      return d.toLocaleString('tr-TR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      })
    }

    const statusVariant = status => {
      if (status === 'success') return 'success'
      if (status === 'failed') return 'danger'
      return 'neutral'
    }

    onMounted(loadLog)

    return {
      log,
      loading,
      parsedError,
      formatTimestamp,
      statusVariant
    }
  }
}
</script>

<style scoped>
.detail-grid {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.detail-item {
  display: flex;
  gap: 12px;
  align-items: flex-start;
}
.detail-item.full-width {
  flex-direction: column;
}
.detail-item label {
  font-size: 12px;
  color: var(--text-secondary);
  min-width: 140px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 600;
}
.detail-item .value {
  font-size: 14px;
  color: var(--text-primary);
  word-break: break-word;
  flex: 1;
}
.value.service {
  color: var(--success);
  font-weight: 600;
}
.value.error-desc {
  color: var(--danger);
}
.user-link {
  color: var(--primary-color);
  text-decoration: none;
}
.user-link:hover {
  text-decoration: underline;
}
.json-display {
  background: var(--background-dark);
  border-radius: var(--radius-sm);
  padding: 16px;
  border: 1px solid var(--panel-border);
}
.json-display pre {
  margin: 0;
  color: var(--text-secondary);
  font-size: 13px;
  line-height: 1.5;
  font-family: 'Courier New', monospace;
  white-space: pre-wrap;
  word-wrap: break-word;
  overflow-x: auto;
}
</style>
